import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Grid3x3 } from "lucide-react";
import { useMemo } from "react";
import './ProcessPrioritizationMatrix.css';

const getQuadrant = (valueScore, effortScore) => {
    if (valueScore >= 6 && effortScore <= 5)
        return { label: "Quick wins", color: "ppm-bubble-green" };
    if (valueScore >= 6 && effortScore > 5)
        return { label: "Strategic", color: "ppm-bubble-blue" };
    if (valueScore < 6 && effortScore <= 5)
        return { label: "Defer", color: "ppm-bubble-yellow" };
    return { label: "Low priority", color: "ppm-bubble-red" };
};

const ProcessPrioritizationMatrix = ({ analysis }) => {
    console.log("ProcessPrioritizationMatrix - analysis:", analysis);
    
    const processes = useMemo(() => {
        const breakdown = analysis?.detailed_process_breakdown ?? [];
        console.log("ProcessPrioritizationMatrix - processes:", breakdown);
        return breakdown;
    }, [analysis]);

    const { minValue, maxValue } = useMemo(() => {
        if (processes.length === 0) {
            return { minValue: 0, maxValue: 0 };
        }
        const totals = processes.map((proc) => {
            const total = proc.total_efficiency_value + proc.total_effectiveness_max;
            console.log(`Process ${proc.process}: efficiency=${proc.total_efficiency_value}, effectiveness=${proc.total_effectiveness_max}, total=${total}`);
            return total;
        });
        return { minValue: Math.min(...totals), maxValue: Math.max(...totals) };
    }, [processes]);

    const positionedBubbles = useMemo(() => {
        if (processes.length === 0) return [];
        
        const bubbles = processes.map((process) => {
            const totalValue = process.total_efficiency_value + process.total_effectiveness_max;
            const normalizedValue = maxValue - minValue === 0 ? 0.5 : (totalValue - minValue) / (maxValue - minValue);
            
            const avgAutomation = process.sub_processes?.length === 0
                ? 0
                : (process.sub_processes?.reduce(
                    (sum, sub) => sum + (sub.efficiency_metrics?.max_automation_percent || 0),
                    0
                ) || 0) / (process.sub_processes?.length || 1);
            
            const normalizedEffort = 1 - avgAutomation / 100;
            
            const padding = 18;
            let x = padding + (normalizedEffort * (100 - 2 * padding));
            let y = padding + ((1 - normalizedValue) * (100 - 2 * padding));
            
            const minSize = 8;
            const maxSize = 18;
            const size = minSize + normalizedValue * (maxSize - minSize);
            
            console.log(`Bubble ${process.process}: x=${x}, y=${y}, size=${size}%, automation=${avgAutomation}`);
            
            return { process, x, y, size, avgAutomation, totalValue };
        });

        const iterations = 80;
        const minDistance = 4;
        
        for (let iter = 0; iter < iterations; iter++) {
            let moved = false;
            
            for (let i = 0; i < bubbles.length; i++) {
                for (let j = i + 1; j < bubbles.length; j++) {
                    const dx = bubbles[j].x - bubbles[i].x;
                    const dy = bubbles[j].y - bubbles[i].y;
                    const distance = Math.sqrt(dx * dx + dy * dy);
                    const minDist = (bubbles[i].size / 2 + bubbles[j].size / 2) + minDistance;
                    
                    if (distance < minDist && distance > 0) {
                        moved = true;
                        const overlap = minDist - distance;
                        const angle = Math.atan2(dy, dx);
                        const moveX = (Math.cos(angle) * overlap) / 2;
                        const moveY = (Math.sin(angle) * overlap) / 2;
                        
                        bubbles[i].x -= moveX;
                        bubbles[i].y -= moveY;
                        bubbles[j].x += moveX;
                        bubbles[j].y += moveY;
                        
                        const iPadding = bubbles[i].size / 2;
                        const jPadding = bubbles[j].size / 2;
                        bubbles[i].x = Math.max(iPadding, Math.min(100 - iPadding, bubbles[i].x));
                        bubbles[i].y = Math.max(iPadding, Math.min(100 - iPadding, bubbles[i].y));
                        bubbles[j].x = Math.max(jPadding, Math.min(100 - jPadding, bubbles[j].x));
                        bubbles[j].y = Math.max(jPadding, Math.min(100 - jPadding, bubbles[j].y));
                    }
                }
            }
            
            if (!moved) break;
        }
        
        console.log("Final positioned bubbles:", bubbles);
        return bubbles;
    }, [processes, minValue, maxValue]);

    const renderBubble = (bubble, index) => {
        const { process, x, y, size, avgAutomation, totalValue } = bubble;
        
        const normalizedValue = maxValue - minValue === 0 ? 0.5 : (totalValue - minValue) / (maxValue - minValue);
        const normalizedEffort = 1 - avgAutomation / 100;
        
        const valueScore = normalizedValue * 9 + 1;
        const effortScore = normalizedEffort * 9 + 1;
        const quadrant = getQuadrant(valueScore, effortScore);
        
        const isTopHalf = y < 50;
        const isLeftHalf = x < 50;
        
        const fontSize = 0.8 + normalizedValue * 0.6;
        
        return (
            <div
                key={`${process.process}-${index}`}
                className="ppm-bubble"
                style={{
                    left: `${x}%`,
                    top: `${y}%`,
                    width: `${size}%`,
                }}
            >
                <div className={`ppm-bubble-circle ${quadrant.color}`}>
                    <span className="ppm-bubble-text" style={{ fontSize: `${fontSize}rem` }}>
                        {process.process}
                    </span>
                </div>
                
                <div className={`ppm-tooltip ${isTopHalf ? 'ppm-tooltip-bottom' : 'ppm-tooltip-top'} ${isLeftHalf ? 'ppm-tooltip-left' : 'ppm-tooltip-right'}`}>
                    <div className="ppm-tooltip-content">
                        <p className="ppm-tooltip-title">{process.process}</p>
                        <div className="ppm-tooltip-body">
                            <p><span className="ppm-tooltip-label">Efficiency:</span> ${(process.total_efficiency_value || 0).toFixed(2)}M</p>
                            <p><span className="ppm-tooltip-label">Effectiveness:</span> ${(process.total_effectiveness_max || 0).toFixed(2)}M</p>
                            <p className="ppm-tooltip-divider">
                                <span className="ppm-tooltip-label">Avg Automation:</span> {avgAutomation.toFixed(1)}%
                            </p>
                            <p><span className="ppm-tooltip-label">Quadrant:</span> <span className="ppm-tooltip-quadrant">{quadrant.label}</span></p>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    console.log("Rendering matrix with", processes.length, "processes");

    return (
        <Card>
            <CardHeader>
                <div className="ppm-header">
                    <Grid3x3 className="ppm-header-icon" />
                    <div>
                        <CardTitle>Process Prioritization Matrix</CardTitle>
                        <CardDescription>
                            Value vs. Effort analysis derived from process-level efficiency and automation inputs
                        </CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                {processes.length === 0 ? (
                    <p className="ppm-empty-text">
                        Process analysis data is not available yet.
                    </p>
                ) : (
                    <div className="ppm-container">
                        <div className="ppm-matrix-wrapper">
                            <div className="ppm-matrix">
                                <div className="ppm-quadrant-backgrounds">
                                    <div className="ppm-quadrant-bg ppm-bg-top-left" />
                                    <div className="ppm-quadrant-bg ppm-bg-top-right" />
                                    <div className="ppm-quadrant-bg ppm-bg-bottom-left" />
                                    <div className="ppm-quadrant-bg ppm-bg-bottom-right" />
                                </div>

                                <div className="ppm-grid-lines">
                                    <div className="ppm-grid-vertical"></div>
                                    <div className="ppm-grid-horizontal"></div>
                                </div>
                                
                                <div className="ppm-quadrant-label ppm-quadrant-top-left">Quick wins</div>
                                <div className="ppm-quadrant-label ppm-quadrant-top-right">Strategic</div>
                                <div className="ppm-quadrant-label ppm-quadrant-bottom-left">Defer</div>
                                <div className="ppm-quadrant-label ppm-quadrant-bottom-right">Low priority</div>
                                
                                {positionedBubbles.map((bubble, index) => renderBubble(bubble, index))}
                            </div>
                            
                            <div className="ppm-axis-label ppm-axis-x">
                                <span>Low Effort</span>
                                <span>High Effort</span>
                            </div>
                            <div className="ppm-axis-label ppm-axis-y">
                                <span>High Value</span>
                                <span>Low Value</span>
                            </div>
                        </div>
                        
                        <div className="ppm-legend">
                            <div className="ppm-legend-item ppm-legend-green">
                                <div className="ppm-legend-circle"></div>
                                <div className="ppm-legend-text">
                                    <p className="ppm-legend-title">Quick wins</p>
                                </div>
                            </div>
                            <div className="ppm-legend-item ppm-legend-blue">
                                <div className="ppm-legend-circle"></div>
                                <div className="ppm-legend-text">
                                    <p className="ppm-legend-title">Strategic</p>
                                </div>
                            </div>
                            <div className="ppm-legend-item ppm-legend-yellow">
                                <div className="ppm-legend-circle"></div>
                                <div className="ppm-legend-text">
                                    <p className="ppm-legend-title">Defer</p>
                                </div>
                            </div>
                            <div className="ppm-legend-item ppm-legend-red">
                                <div className="ppm-legend-circle"></div>
                                <div className="ppm-legend-text">
                                    <p className="ppm-legend-title">Low priority</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default ProcessPrioritizationMatrix;
